import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import BusinessesAdd from "./BusinessesAdd";
import BusinessesEdit from "./BusinessesEdit";
import ApiServices from "../../../Services/ApiServices/ApiServices";

const BusinessesList = () => {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const debounceRef = useRef(null);

  const fetchBusinesses = async (q = "") => {
    setLoading(true);
    try {
      const url = q ? `external-businesses/?search=${encodeURIComponent(q)}` : "external-businesses/";
      const res = await ApiServices.get(url);
      const data = res?.data ?? res;
      setBusinesses(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      let msg = "Failed to fetch businesses.";
      if (err?.response?.data?.detail) msg = err.response.data.detail;
      else if (err?.message) msg = err.message;
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBusinesses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchBusinesses(search);
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Handlers for dialogs
  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const handleOpenEdit = (business) => {
    setSelectedBusiness(business);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setSelectedBusiness(null);
    setOpenEdit(false);
  };

  // Delete flow
  const handleDeleteClick = (business) => {
    setDeleteTarget(business);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setConfirmOpen(false);
    setLoading(true);
    try {
      await ApiServices.delete(`external-businesses/${deleteTarget.id}/`);
      setSnack({ open: true, message: "Business deleted", severity: "success" });
      await fetchBusinesses(search);
    } catch (err) {
      console.error(err);
      let msg = "Delete failed.";
      if (err?.response?.data?.detail) msg = err.response.data.detail;
      else if (err?.message) msg = err.message;
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setLoading(false);
      setDeleteTarget(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setDeleteTarget(null);
  };

  // Callbacks from Add / Edit components
  const onAddSuccess = async (msg = "Business added") => {
    setSnack({ open: true, message: msg, severity: "success" });
    setOpenAdd(false);
    await fetchBusinesses(search);
  };

  const onEditSuccess = async (msg = "Business updated") => {
    setSnack({ open: true, message: msg, severity: "success" });
    handleCloseEdit();
    await fetchBusinesses(search);
  };

  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  return (
    <Box sx={containerStyles}>
      <style>{`
        .businesses-header {
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:12px;
          margin-bottom:16px;
          flex-wrap:wrap;
        }
        .search-field { max-width:420px; width:100%; }
        .add-btn { min-width:160px; }
        .table-wrap { width:100%; }
      `}</style>

      <Box className="businesses-header">
        <Typography variant="h5" sx={{ color: "#094c9f", fontWeight: 700 }}>
          Businesses
        </Typography>

        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            className="search-field"
            placeholder="Search businesses..."
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            className="add-btn"
            sx={{ backgroundColor: "#094c9f", "&:hover": { backgroundColor: "#083b82" } }}
          >
            Add New Business
          </Button>
        </Box>
      </Box>

      <Paper sx={{ width: "100%", overflow: "hidden" }} elevation={2}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            sx={{
              maxHeight: "64vh",
              overflowX: "auto",
            }}
          >
            <Table
              stickyHeader
              sx={{
                minWidth: 800,
                tableLayout: "fixed",
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: "60px" }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: "220px" }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: "220px" }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: "140px" }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: "32%" }}>Address</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: "140px" }}>Profit %</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, width: "120px" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {businesses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No businesses found.
                    </TableCell>
                  </TableRow>
                ) : (
                  businesses.map((b, idx) => (
                    <TableRow key={b.id} hover>
                      <TableCell sx={{ width: "60px" }}>{idx + 1}</TableCell>

                      <TableCell sx={{ width: "220px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {b.name}
                      </TableCell>

                      <TableCell sx={{ width: "220px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {b.email}
                      </TableCell>

                      <TableCell sx={{ width: "140px" }}>{b.phone}</TableCell>

                      <TableCell
                        sx={{
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          maxWidth: { xs: "140px", sm: "260px", md: "420px" },
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {b.address}
                      </TableCell>

                      <TableCell sx={{ width: "140px" }}>{parseFloat(b.profit_share_percentage).toFixed(2)}%</TableCell>

                      <TableCell align="right" sx={{ width: "120px" }}>
                        <IconButton onClick={() => handleOpenEdit(b)} aria-label="edit" size="small">
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleDeleteClick(b)} aria-label="delete" size="small">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Add dialog */}
      <Dialog open={openAdd} onClose={handleCloseAdd} maxWidth="md" fullWidth>
        <DialogTitle>Add New Business</DialogTitle>
        <DialogContent>
          <BusinessesAdd onSuccess={onAddSuccess} onCancel={handleCloseAdd} />
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={openEdit} onClose={handleCloseEdit} maxWidth="md" fullWidth>
        <DialogTitle>Edit Business</DialogTitle>
        <DialogContent>
          {selectedBusiness && (
            <BusinessesEdit business={selectedBusiness} onSuccess={onEditSuccess} onCancel={handleCloseEdit} />
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm delete dialog */}
      <Dialog open={confirmOpen} onClose={handleCancelDelete}>
        <DialogTitle>Confirm delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button color="error" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4500} onClose={closeSnack}>
        <Alert onClose={closeSnack} severity={snack.severity} sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BusinessesList;

const containerStyles = {
  p: 3,
  width: "100%",
  minHeight: "70vh",
  background: "#fff",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gap: 2,
};
